<template>
  <Modal
    ref="modal"
    :dismissible="true"
    :show-footer="false"
    @modal:opened="handleModalOpened"
  >
    <template #activator="{ show }">
      <button
        class="p-2 block text-sm font-medium text-gray-600 hover:text-primary-600 hover:bg-gray-50 transition-colors relative"
        title="Automation logs"
        @click="show"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          height="24px"
          viewBox="0 -960 960 960"
          width="24px"
          fill="currentColor"
        >
          <path
            d="M160-360q-50 0-85-35t-35-85q0-50 35-85t85-35v-80q0-33 23.5-56.5T240-760h120q0-50 35-85t85-35q50 0 85 35t35 85h120q33 0 56.5 23.5T800-680v80q50 0 85 35t35 85q0 50-35 85t-85 35v160q0 33-23.5 56.5T720-120H240q-33 0-56.5-23.5T160-200v-160Zm200-80q25 0 42.5-17.5T420-500q0-25-17.5-42.5T360-560q-25 0-42.5 17.5T300-500q0 25 17.5 42.5T360-440Zm240 0q25 0 42.5-17.5T660-500q0-25-17.5-42.5T600-560q-25 0-42.5 17.5T540-500q0 25 17.5 42.5T600-440ZM320-280h320v-80H320v80Zm-80 80h480v-480H240v480Zm240-240Z"
          />
        </svg>

        <span
          v-if="activeJobsCount > 0"
          class="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full min-w-[20px] h-5 flex items-center justify-center px-1"
        >
          {{ activeJobsCount > 99 ? '99+' : activeJobsCount }}
        </span>
      </button>
    </template>

    <template #header>
      <div class="flex items-center justify-between w-full">
        <div class="flex items-center gap-2">
          <span>Jobs Status</span>
          <span
            v-if="loading"
            class="text-sm text-gray-500 font-normal animate-pulse"
          >
            Loading...
          </span>
        </div>
        <button
          :disabled="loading"
          class="px-3 py-1.5 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          @click="fetchJobs"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
              clip-rule="evenodd"
            />
          </svg>
          Refresh
        </button>
      </div>
    </template>

    <template #content>
      <div class="min-w-[50rem] h-[70vh] flex flex-col">
        <div class="flex items-center justify-between border-b pb-4">
          <TabStyle2
            :tabs="tabs"
            :active-tab-number="activeTab"
            @tab:clicked="handleTabChange"
          />

          <div class="flex items-center gap-2">
            <label class="text-sm text-gray-600">Status:</label>
            <select
              v-model="selectedStatus"
              class="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Statuses</option>
              <option value="waiting">Waiting</option>
              <option value="active">In Progress</option>
              <option v-if="isCuttingEdge" value="failed">Failed</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        <div class="mt-4 flex-1 overflow-y-auto">
          <div v-if="loading" class="text-center py-8 text-gray-500">
            Loading jobs...
          </div>

          <div v-else-if="error" class="text-center py-8 text-red-500">
            {{ error }}
          </div>

          <div
            v-else-if="jobs.length === 0"
            class="text-center py-8 text-gray-500"
          >
            No jobs found
          </div>

          <div v-else class="space-y-2 px-2">
            <div
              v-for="job in jobs"
              :key="job.id"
              class="p-3 border rounded-lg"
              :class="{
                'border-blue-200 bg-blue-50': job.status === 'active',
                'border-yellow-200 bg-yellow-50': job.status === 'waiting',
                'border-green-200 bg-green-50': job.status === 'completed',
                'border-red-200 bg-red-50': job.status === 'failed',
              }"
            >
              <div class="flex justify-between items-start">
                <div class="flex-1">
                  <div class="flex items-center gap-2">
                    <span class="font-medium text-sm">
                      {{ getOperationLabel(job.operation) }}
                    </span>
                    <span
                      class="text-xs px-2 py-0.5 rounded"
                      :class="{
                        'bg-blue-500 text-white': job.status === 'active',
                        'bg-yellow-500 text-white': job.status === 'waiting',
                        'bg-green-500 text-white': job.status === 'completed',
                        'bg-red-500 text-white': job.status === 'failed',
                      }"
                    >
                      {{ job.status === 'active' ? 'in progress' : job.status }}
                    </span>
                  </div>
                  <div class="text-xs text-gray-600 mt-1">
                    <RouterLink
                      v-if="job.reviewItemId"
                      :to="{
                        name: RouteName.REVIEW_ITEM,
                        params: {
                          reviewItemId: job.reviewItemId,
                          id: reviewId,
                        },
                      }"
                      @click="modal?.hide()"
                    >
                      Review item #{{ job.reviewItemId }}
                    </RouterLink>
                  </div>
                  <div
                    v-if="isCuttingEdge && job.error"
                    class="text-xs text-red-600 mt-1"
                  >
                    Error: {{ job.error }}
                  </div>
                  <div v-if="isCuttingEdge" class="text-xs text-gray-500 mt-1">
                    Created: {{ formatDate(job.createdAt) }}
                    <span v-if="job.startedAt" class="ml-2">
                      Started: {{ formatDate(job.startedAt) }}
                    </span>
                    <span v-if="job.completedAt" class="ml-2">
                      Completed: {{ formatDate(job.completedAt) }}
                    </span>
                    <span v-if="job.failedAt" class="ml-2">
                      Failed: {{ formatDate(job.failedAt) }}
                    </span>
                  </div>
                </div>
                <div v-if="isCuttingEdge" class="text-xs text-gray-500">
                  <span v-if="job.status !== 'completed'">
                    Attempts: {{ job.attempts }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="!loading && jobs.length > 0"
          class="flex items-center justify-between border-t pt-4 mt-4"
        >
          <div class="text-sm text-gray-600">
            Showing {{ (currentPage - 1) * pageSize + 1 }}-{{
              Math.min(currentPage * pageSize, totalJobs)
            }}
            of {{ totalJobs }}
          </div>
          <div class="flex items-center gap-2">
            <button
              :disabled="currentPage === 1"
              class="px-3 py-1 text-sm border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              @click="previousPage"
            >
              Previous
            </button>
            <span class="text-sm text-gray-600">
              Page {{ currentPage }} of {{ totalPages }}
            </span>
            <button
              :disabled="currentPage >= totalPages"
              class="px-3 py-1 text-sm border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              @click="nextPage"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { RouterLink } from 'vue-router'
import Modal from '@app/components/Global/Modal/Modal.vue'
import TabStyle2 from '@app/components/Global/Tabs/TabStyle2.vue'
import { Api } from '@evidence/sdk'
import { injectStrict } from '@app/utils/injectStrict'
import { DevToolboxKey } from '@app/injectionKeys'
import { ReviewKey } from '@app/views/Review/use-review'
import { RouteName } from '@app/router'

const { isCuttingEdge } = injectStrict(DevToolboxKey)
const { reviewItemJobStatuses } = injectStrict(ReviewKey)
const api = new Api()

const props = defineProps<{
  reviewId: number
}>()

const modal = ref<InstanceType<typeof Modal>>()
const loading = ref(false)
const error = ref<string | null>(null)
const jobs = ref<any[]>([])
const activeTab = ref(0)
const selectedStatus = ref('')
const currentPage = ref(1)
const pageSize = ref(20)
const totalJobs = ref(0)

const tabs = computed(() => [
  { number: 0, title: 'All' },
  { number: 1, title: 'Auto Screening' },
  { number: 2, title: 'Auto Extraction' },
  { number: 3, title: 'Auto Synthesis' },
  { number: 4, title: 'Auto Synthesis Delta' },
  { number: 5, title: 'PDF Retrieval' },
])

const totalPages = computed(() => Math.ceil(totalJobs.value / pageSize.value))

const activeJobsCount = computed(() => {
  let count = 0
  for (const itemStatuses of Object.values(reviewItemJobStatuses.value)) {
    for (const job of Object.values(itemStatuses)) {
      if (
        job?.status === 'waiting' ||
        job?.status === 'active' ||
        job?.status === 'paused'
      ) {
        count++
      }
    }
  }
  return count
})

async function fetchJobs() {
  loading.value = true
  error.value = null

  try {
    const operationMap = [
      '',
      'auto-screen',
      'auto-extract',
      'auto-synthesis',
      'auto-synthesis-delta',
      'pdf-retrieval',
    ]
    const operation =
      activeTab.value !== 0 ? operationMap[activeTab.value] : undefined

    let statusFilter = selectedStatus.value
    if (!statusFilter && !isCuttingEdge) {
      statusFilter = 'waiting,active,completed'
    }

    const response = await api.api.jobsControllerGetJobs({
      reviewId: props.reviewId.toString(),
      operation,
      status: statusFilter || undefined,
      page: currentPage.value.toString(),
      pageSize: pageSize.value.toString(),
    } as any)

    jobs.value = response.data?.jobs || []
    totalJobs.value = response.data?.total || 0
  } catch (err: any) {
    error.value = err.message || 'Failed to load jobs'
  } finally {
    loading.value = false
  }
}

function handleModalOpened() {
  currentPage.value = 1
  fetchJobs()
}

function handleTabChange(tab: { number: number }) {
  activeTab.value = tab.number
  currentPage.value = 1
  fetchJobs()
}

function previousPage() {
  if (currentPage.value > 1) {
    currentPage.value--
    fetchJobs()
  }
}

function nextPage() {
  if (currentPage.value < totalPages.value) {
    currentPage.value++
    fetchJobs()
  }
}

function getOperationLabel(operation: string): string {
  const labels: Record<string, string> = {
    'auto-screen': 'Auto Screening',
    'auto-extract': 'Auto Extraction',
    'auto-synthesis': 'Auto Synthesis',
    'auto-synthesis-delta': 'Auto Synthesis Delta',
    'pdf-retrieval': 'PDF Retrieval',
  }
  return labels[operation] || operation
}

function formatDate(date: string | Date | null | undefined): string {
  if (!date) return 'N/A'
  return new Date(date).toLocaleString()
}

watch(selectedStatus, () => {
  currentPage.value = 1
  fetchJobs()
})

defineExpose({
  show: () => modal.value?.show(),
  hide: () => modal.value?.hide(),
})
</script>
